import React, { useState, useEffect } from 'react';
import { Search, Printer, Download, CreditCard, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const AdmitCards = () => {
  const [filters, setFilters] = useState({ exam_id: '', course_id: '', semester_id: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [admitCards, setAdmitCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  useEffect(() => {
    fetchAdmitCards();
  }, [filters]);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      const [examsRes, coursesRes, semestersRes] = await Promise.all([
        axios.get(`${API_URL}/api/examination/exams`, { headers }),
        axios.get(`${API_URL}/api/master-settings/courses`, { headers }),
        axios.get(`${API_URL}/api/master-settings/semesters`, { headers })
      ]);

      setExams(examsRes.data || []);
      setCourses(coursesRes.data || []);
      setSemesters(semestersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmitCards = async () => {
    try {
      const params = {};
      if (filters.exam_id) params.exam_id = filters.exam_id;
      if (filters.course_id) params.course_id = filters.course_id;

      const response = await axios.get(`${API_URL}/api/examination/admit-cards`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });

      setAdmitCards(response.data || []);
    } catch (error) {
      console.error('Error fetching admit cards:', error);
    }
  };

  const handleGenerateAdmitCards = async () => {
    if (!filters.exam_id) {
      alert('Please select an exam first');
      return;
    }

    setGenerating(true);
    try {
      const requestData = {
        exam_id: filters.exam_id,
        course_id: filters.course_id || undefined,
        semester_id: filters.semester_id || undefined
      };

      const response = await axios.post(
        `${API_URL}/api/examination/admit-cards/generate`,
        requestData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setGenerationResult(response.data);
      setShowGenerateModal(false);
      fetchAdmitCards();
      
      // Show results
      const result = response.data;
      alert(
        `Admit Card Generation Complete!\n\n` +
        `✅ Generated: ${result.generated_count}\n` +
        `❌ Ineligible: ${result.ineligible_count}\n` +
        `Total Students: ${result.total_students}`
      );
    } catch (error) {
      console.error('Error generating admit cards:', error);
      alert(error.response?.data?.detail || 'Failed to generate admit cards');
    } finally {
      setGenerating(false);
    }
  };

  const filteredCards = admitCards.filter(card =>
    card.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.admit_card_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const printAdmitCard = (card) => {
    // Use collegeInfo from state instead of localStorage
    const tenantInfo = collegeInfo || {};
    
    const printWindow = window.open('', '_blank');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Admit Card - ${card.admit_card_number}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .tenant-header {
              text-align: center;
              margin-bottom: 15px;
              padding-bottom: 15px;
              border-bottom: 1px solid #ddd;
            }
            .tenant-header img { max-height: 60px; margin-bottom: 8px; }
            .tenant-header h2 { margin: 5px 0; color: #1e40af; font-size: 20px; }
            .tenant-header p { margin: 2px 0; font-size: 11px; color: #666; }
            .tenant-header .website { color: #2563eb; }
            .header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 3px solid #2563eb;
              padding-bottom: 20px;
            }
            .header h1 { 
              color: #1e40af; 
              margin: 0;
              font-size: 28px;
            }
            .header h2 { 
              color: #3b82f6; 
              margin: 10px 0 0 0;
              font-size: 20px;
            }
            .content { 
              margin: 30px 0; 
            }
            .row { 
              display: flex; 
              padding: 12px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .label { 
              font-weight: bold; 
              width: 200px;
              color: #374151;
            }
            .value { 
              flex: 1;
              color: #1f2937;
            }
            .instructions {
              margin-top: 30px;
              padding: 20px;
              background-color: #fef3c7;
              border-left: 4px solid #f59e0b;
            }
            .instructions h3 {
              margin-top: 0;
              color: #92400e;
            }
            .instructions ul {
              margin: 10px 0;
              padding-left: 20px;
            }
            .instructions li {
              margin: 8px 0;
              color: #78350f;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 12px;
              color: #6b7280;
            }
            @media print {
              body { padding: 20px; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="tenant-header">
            ${tenantInfo.logo_url ? `<img src="${tenantInfo.logo_url}" alt="Logo" />` : ''}
            <h2>${tenantInfo.college_name || tenantInfo.name || 'Institution Name'}</h2>
            <p>${tenantInfo.address || ''}</p>
            <p>${tenantInfo.phone ? 'Phone: ' + tenantInfo.phone : ''} ${tenantInfo.email ? ' | Email: ' + tenantInfo.email : ''}</p>
            ${tenantInfo.website ? `<p class="website">${tenantInfo.website}</p>` : ''}
          </div>
          <div class="header">
            <h1>ADMIT CARD</h1>
            <h2>${card.exam_name}</h2>
            <p style="margin: 5px 0; color: #6b7280;">${card.exam_start_date} to ${card.exam_end_date}</p>
          </div>
          
          <div class="content">
            <div class="row">
              <div class="label">Admit Card Number:</div>
              <div class="value">${card.admit_card_number}</div>
            </div>
            <div class="row">
              <div class="label">Student Name:</div>
              <div class="value">${card.student_name}</div>
            </div>
            <div class="row">
              <div class="label">Admission Number:</div>
              <div class="value">${card.admission_number || 'N/A'}</div>
            </div>
            <div class="row">
              <div class="label">Roll Number:</div>
              <div class="value">${card.roll_number || 'N/A'}</div>
            </div>
            <div class="row">
              <div class="label">Issue Date:</div>
              <div class="value">${card.issue_date}</div>
            </div>
            <div class="row">
              <div class="label">Status:</div>
              <div class="value" style="color: #16a34a; font-weight: bold;">${card.status}</div>
            </div>
          </div>

          <div class="instructions">
            <h3>⚠️ Important Instructions</h3>
            <ul>
              ${card.instructions?.map(inst => `<li>${inst}</li>`).join('') || ''}
            </ul>
          </div>

          <div class="footer">
            <p>This is a computer generated admit card and does not require signature.</p>
            <p>Please verify all details carefully. Contact examination office for any discrepancies.</p>
          </div>

          <div style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()" style="background: #2563eb; color: white; padding: 10px 30px; border: none; border-radius: 8px; cursor: pointer; font-size: 16px;">
              Print Admit Card
            </button>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admit Cards</h1>
          <p className="text-sm text-gray-500 mt-1">Generate and manage examination admit cards</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <CreditCard className="h-4 w-4" />
          Generate Admit Cards
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Admit Cards</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{admitCards.length}</p>
            </div>
            <CreditCard className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Issued</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {admitCards.filter(c => c.status === 'Issued').length}
              </p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Exams</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">
                {exams.filter(e => e.is_active).length}
              </p>
            </div>
            <AlertTriangle className="h-12 w-12 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Filtered Results</p>
              <p className="text-3xl font-bold text-gray-600 mt-1">{filteredCards.length}</p>
            </div>
            <Search className="h-12 w-12 text-gray-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exam</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.exam_id}
              onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
            >
              <option value="">All Exams</option>
              {exams.map(exam => (
                <option key={exam.id} value={exam.id}>{exam.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id}
              onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Semester</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id}
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
            >
              <option value="">All Semesters</option>
              {semesters.filter(s => !filters.course_id || s.course_id === filters.course_id).map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or number"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admit Card #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roll Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue Date</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredCards.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">No admit cards found</td>
                </tr>
              ) : (
                filteredCards.map((card) => (
                  <tr key={card.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{card.admit_card_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{card.student_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{card.roll_number || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{card.exam_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{card.issue_date}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        card.status === 'Issued' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {card.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => printAdmitCard(card)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Print Admit Card"
                      >
                        <Printer className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Generate Admit Cards</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Exam *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={filters.exam_id}
                  onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
                >
                  <option value="">Select Exam</option>
                  {exams.map(exam => (
                    <option key={exam.id} value={exam.id}>{exam.name} - {exam.exam_type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course (Optional)</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={filters.course_id}
                  onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
                >
                  <option value="">All Courses</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Semester (Optional)</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={filters.semester_id}
                  onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
                >
                  <option value="">All Semesters</option>
                  {semesters.filter(s => !filters.course_id || s.course_id === filters.course_id).map(sem => (
                    <option key={sem.id} value={sem.id}>{sem.name}</option>
                  ))}
                </select>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Fee Verification:</strong> Only students with cleared dues and paid exam fee will receive admit cards.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateAdmitCards}
                disabled={generating || !filters.exam_id}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmitCards;